/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable no-param-reassign */
/* eslint-disable react/no-array-index-key */
import moment from 'moment'
import React, { useState, useEffect } from 'react'
import { Tooltip } from '@mui/material'
import { Modal } from 'components'
import Check from '@mui/icons-material/Check'
import Close from '@mui/icons-material/Close'
import { Policy, InfoOutlined } from '@mui/icons-material'
import withStyles from '@mui/styles/withStyles'

import ManualAnalysisList from 'components/ManualAnalysisList'
import { RFC_FIELDS } from 'utils/constants/rfcFields'
import { BGC_FIELDS } from 'utils/constants/bgcFields'
import {
  APPLICATION_RESULT,
  APPLICATION_RESULT_DESCRIPTION,
  APPLICATION_STATUS,
  APPLICATION_STATUS_DESCRIPTION,
  ITEM_STATUS,
} from 'utils/constants/application'
import { buildScore, maskString } from 'utils/functions'
import classNames from 'classnames'
import Timeline from 'components/Timeline'
import Colors from 'utils/Colors'
import {
  showApprovedIcon,
  showDashIcon,
  showDashIcon as showEmptyIcon,
  showErrorIcon,
  showGreenhouseStatusIcon,
  showReprovedIcon,
  showWaitingIcon,
} from 'components/CommonIcon'
import styles from './styles'
import ResultBox from './ResultBox'
import ResultStatus from './ResultStatus'
import DownloadAppButton from './DownloadAppButton'
import ExpandAppButton from './ExpandAppButton'
import ApplicationReviewsButton from './AppliactionReviewsButton'
import GreenhouseStage from './GreenhouseStage'

const ResumeTable = ({ data, handleOpenModal, reloadData }) => {
  const classes = styles()

  const [modal, setModal] = useState(false)
  const [applicationId, setApplicationId] = useState(null)
  const [checkType, setCheckType] = useState(null)

  const CustomTooltip = withStyles(() => ({
    tooltip: {
      margin: 0,
    },

  }))(Tooltip)

  const handleCloseModal = () => {
    setModal(false)
    reloadData()
  }

  const buildBgcFields = (application) => {
    const summary = application.background_check_summary

    const showBgcProcessmentIcon = (field) => {
      const { status, consists } = summary[field]
      const inDenyList = application.is_in_deny_list

      if (field !== BGC_FIELDS.compliance_list && inDenyList) {
        return showDashIcon()
      }
      if (status === ITEM_STATUS.processing) return showWaitingIcon()
      switch (consists) {
        case true:
          return showReprovedIcon()
        case false:
          return showApprovedIcon()
        default:
          return showEmptyIcon()
      }
    }

    const showBgcRiskScore = () => {
      if (application.is_in_deny_list) {
        return showDashIcon()
      }
      if (!summary) {
        return null
      }
      if (summary.is_score_inconsistent) {
        return showErrorIcon()
      }
      if (!summary.is_score_complete) {
        return showWaitingIcon()
      }

      if (summary.risk_score) {
        return (
          <div
            className={classes.score}
            style={buildScore(application.background_check_summary.risk_score)}
          >
            {application.background_check_summary.risk_score.toFixed(1)}
          </div>
        )
      }
      return <div>{showDashIcon()}</div>
    }

    const showBgcItemIcon = (field) =>
      summary[field].warnings ? showErrorIcon() : showBgcProcessmentIcon(field)
    try {
      return Object.keys(BGC_FIELDS).map((field, index) => {
        if (
          field === BGC_FIELDS.job_role ||
          field === BGC_FIELDS.occupation_area
        ) {
          return null
        }
        return (
          <td key={`bgc_table:${index}`}>
            {field === BGC_FIELDS.risk_score
              ? showBgcRiskScore()
              : showBgcItemIcon(field)}
          </td>
        )
      })
    } catch (err) {
      return <td>N/A</td>
    }
  }

  const buildRfcFields = (application) => {
    const showRfcProcessmentIcon = (status, consists) => {
      if (status === ITEM_STATUS.processing) return showWaitingIcon()
      switch (consists) {
        case true:
          return showReprovedIcon()
        case false:
          return showApprovedIcon()
        default:
          return showEmptyIcon()
      }
    }

    const hasFinancialWarnings =
      application.reference_check_summary[RFC_FIELDS.financial_pendings_data]
        .warnings
    const hasFinancialPendings =
      application.reference_check_summary[RFC_FIELDS.financial_pendings_data]
        .consists
    const score =
      application.reference_check_summary[RFC_FIELDS.credit_score_data]
        .quod_score
    try {
      return (
        <React.Fragment>
          <td
            key={`rfc_table:${RFC_FIELDS.credit_score_data}`}
            style={{
              color: score >= 600 ? Colors.green : Colors.red,
              fontWeight: 550,
            }}
          >
            {score || showEmptyIcon()}
          </td>
          <td key={`rfc_table:${RFC_FIELDS.financial_pendings_data}`}>
            {hasFinancialWarnings
              ? showErrorIcon()
              : typeof hasFinancialPendings === 'boolean'
                ? showRfcProcessmentIcon(application.status, hasFinancialPendings)
                : showEmptyIcon()}
          </td>
        </React.Fragment>
      )
    } catch (err) {
      return <td>N/A</td>
    }
  }

  const showStatus = (application) => {
    switch (application.status) {
      case APPLICATION_STATUS.created:
        return APPLICATION_STATUS_DESCRIPTION.created
      case APPLICATION_STATUS.bgcProcessing:
        return APPLICATION_STATUS_DESCRIPTION.bgcProcessing
      case APPLICATION_STATUS.bgcReported:
      case APPLICATION_STATUS.bgcProcessed:
        return APPLICATION_STATUS_DESCRIPTION.bgcProcessed
      case APPLICATION_STATUS.rfcProcessing:
        return APPLICATION_STATUS_DESCRIPTION.rfcProcessing
      case APPLICATION_STATUS.rfcProcessed:
      case APPLICATION_STATUS.rfcReported:
        return APPLICATION_STATUS_DESCRIPTION.rfcProcessed
      default:
        return showEmptyIcon()
    }
  }

  const handleOpenManualAnalisys = (event) => {
    const info = event.target.id.split('|')
    setApplicationId(info[0])
    setCheckType(info[1])
  }

  const getCheckType = (bgcSummary, rfcSummary) => {
    if (
      bgcSummary.result === APPLICATION_RESULT.waitingForReview ||
      bgcSummary.is_score_inconsistent
    )
      return 'bgc'
    if (rfcSummary.result === APPLICATION_RESULT.waitingForReview) return 'rfc'
    return null
  }

  const showResult = (application) => {
    const bgcSummary = application.background_check_summary
    const rfcSummary = application.reference_check_summary
    const check = getCheckType(bgcSummary, rfcSummary)

    const showOnGoingTooltip = () => (
      <Tooltip
        arrow
        fontSize='small'
        title={<h4>Aguardando movimentação de estágios na Greenhouse para prosseguir</h4>}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center' }}>
          <InfoOutlined fontSize='small' />
          <p>Aguardando Greenhouse</p>
        </span>
      </Tooltip>
    )
    

    if (!application.result) {
      if (
        bgcSummary.result === APPLICATION_RESULT.waitingForReview ||
        rfcSummary.result === APPLICATION_RESULT.waitingForReview ||       
        bgcSummary.score_result === APPLICATION_RESULT.waitingForReview
      )
        return (
          <ResultBox
            id={application.uuid}
            checkType={check}
            onClick={handleOpenManualAnalisys}
            backgroundColor={Colors.blue}
            borderColor={Colors.darkBlue}
            icon={<Policy fontSize="small" />}
          />
        )
      return showOnGoingTooltip()
    }
    if (application.result === APPLICATION_RESULT.approved) {
      return (
        <ResultStatus
          text={APPLICATION_RESULT_DESCRIPTION.approved}
          color={Colors.green}
          icon={<Check fontSize="small" />}
        />
      )
    }

    return (
      <ResultStatus
        text={APPLICATION_RESULT_DESCRIPTION.reproved}
        color={Colors.red}
        icon={<Close fontSize="small" />}
      />
    ) // TODO: mostrar um botao para fazer a analise
  }

  useEffect(() => {
    if (applicationId && checkType) setModal(true)
  }, [applicationId, checkType])

  const showButtonsTableSection = (application) => (
    <div className={classes.detailsContent}>
      <ApplicationReviewsButton application={application} />

      <DownloadAppButton
        applicationUUID={application.uuid}
        canDownload={application.is_report_available}
      />

      <ExpandAppButton
        application={application}
        handleOpenModal={handleOpenModal}
      />
    </div>
  )

  return (
    <React.Fragment>
      <table className={classes.dataTable}>
        <thead>
        <tr>
          <th colSpan={10} />
          <th colSpan={6} className={classes.manualAnalisysHeader}>
            <span></span>
          </th>
          <th colSpan={4} />
        </tr>
        <tr>
          <th colSpan={3} />
          <th colSpan={7} className={classes.scoreRelatedHeader}>
            {' '}
            composição do score{' '}
          </th>
          <th colSpan={3} className={classes.bgcRelatedHeader}>
            {' '}
            Background Check
          </th>
          <th colSpan={2} className={classes.rfcRelatedHeader}>
            {' '}
            Análise de Crédito
          </th>
          <th colSpan={5} />
        </tr>
        <tr>
          <th>Início do Processo</th>
          <th className={classes.nameHeader}>Nome</th>
          <th className={classes.cpfHeader}>CPF</th>
          <th className={classes.scoreHeaders}>Área de Atuação</th>
          <th className={classes.scoreHeaders}>Cargo</th>
          <th
            className={classNames(classes.iconContent, classes.scoreHeaders)}
          >
            Compliance List
          </th>
          <th
            className={classNames(classes.iconContent, classes.scoreHeaders)}
          >
            Mídia Negativa
          </th>
          <th
            className={classNames(classes.iconContent, classes.scoreHeaders)}
          >
            PEP
          </th>
          <th
            className={classNames(classes.iconContent, classes.scoreHeaders)}
          >
            Processos Judiciais
          </th>
          <th
            className={classNames(classes.iconContent, classes.scoreHeaders)}
            style={{ borderRight: '1px solid #FFFFFF' }}
          >
            Listas Restritivas
          </th>
          <th className={classes.bgcHeaders}>Score</th>
          <th className={classNames(classes.iconContent, classes.bgcHeaders)}>
            Pessoa vinculada
          </th>
          <th
            className={classNames(classes.iconContent, classes.scoreHeaders)}
            style={{ borderRight: '1px solid #FFFFFF' }}
          >
            Participação societária
          </th>
          <th className={classNames(classes.iconContent, classes.rfcHeaders)}>
            Score de crédito
          </th>
          <th className={classNames(classes.iconContent, classes.rfcHeaders)}>
            Pendências financeiras
          </th>
          <th>Contratação Greenhouse</th>
          <th>Estágio Greenhouse</th>
          <th>Andamento</th>
          <th>Status</th>
          <th>Detalhes</th>
        </tr>
        </thead>
        <tbody>
        {data ? (
          data.map((application) => {
            const showPersonName = () => {
              const name = application?.candidate?.person?.name
              if (name) {
                return name
              }
              return application.is_in_deny_list
                ? showDashIcon()
                : showWaitingIcon()
            }

            return (
              <CustomTooltip
                interactive="true"
                key={application.greenhouse_id}
                classes={{ tooltip: classes.noMaxWidth }}
                PopperProps={{ style: { zIndex: 0 } }}
                title={
                  <Timeline
                    actualStep={showStatus(application)}
                    steps={APPLICATION_STATUS_DESCRIPTION}
                  />
                }
              >
                <tr className={classes.cursorPointer}>
                  <td>
                    {moment(application.created).format('DD/MM/YYYY')}
                    <br />
                    {moment(application.created).format('HH:mm')}
                  </td>

                  <td>{showPersonName()}</td>

                  <td>
                    {application.candidate.person.cpf
                      ? maskString(application.candidate.person.cpf, 'cpf')
                      : showWaitingIcon()}
                  </td>

                  <td>
                    {application.occupation_area
                      ? application.occupation_area
                      : showWaitingIcon()}
                  </td>

                  <td>
                    {application.job_role
                      ? application.job_role.split(' | ', 1)[0]
                      : application.background_check_summary.job_role
                        ?.warnings
                        ? showErrorIcon()
                        : showWaitingIcon()}
                  </td>

                  {buildBgcFields(application)}

                  {buildRfcFields(application)}

                  <td>
                    {showGreenhouseStatusIcon(application.greenhouse_status)}
                  </td>

                  <td><GreenhouseStage application={application} /></td>

                  <td>{showStatus(application)}</td>

                  <td>{showResult(application)}</td>

                  <td>{showButtonsTableSection(application)}</td>
                </tr>
              </CustomTooltip>
            )
          })
        ) : (
          <tr>
            <td colSpan="20" className={classes.emptyTable}>
              Nenhum resultado encontrado
            </td>
          </tr>
        )}
        </tbody>
      </table>

      <Modal
        open={modal}
        onClose={() => handleCloseModal()}
        title={`Revisão Manual de ${checkType === 'bgc' ? ' Background Check' : 'Análise de Crédito'}`}
      >
        <ManualAnalysisList
          applicationId={applicationId}
          checkType={checkType}
        />
      </Modal>
    </React.Fragment>
  )
}

export default ResumeTable
